import React, { useEffect, useMemo, useRef, useState } from "react";
import { getIdTokenResult, onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, db, storage } from "../../../firebaseConfig";
import "./NafedCeoDashboard.css";

const COLLECTIONS = Object.freeze({
  projects: "godown_projects",
  users: "nafed_users",
  portalUsers: "portal_users",
  allocations: "pacs_pmc_allocations",
  activity: "nafed_activity",
  documents: "nafed_documents",
  dprs: "published_dprs",
  mous: "nafed_mous",
});

/*
 * Existing Shine Agri installations use both nafed_users and portal_users.
 * Older admin/CEO accounts may also keep their authority in a custom claim,
 * userRole, accountRole, designation, department or roles array. Keep every
 * legacy login working without opening the desk to ordinary signed-in users.
 */
const ALLOWED_ROLES = new Set([
  "HEAD_OFFICE",
  "HEAD OFFICE",
  "NAFED_HEAD",
  "NAFED HEAD",
  "NAFED_HEAD_OFFICE",
  "NAFED HEAD OFFICE",
  "CEO",
  "CEO_MASTER",
  "CEO MASTER",
  "CIO",
  "ADMIN",
  "SUPER_ADMIN",
  "SUPER ADMIN",
  "NAFED_ADMIN",
  "NAFED ADMIN",
]);

/* Existing Firebase Auth CEO logins shown by the live portal diagnostics. */
const CEO_AUTH_UIDS = new Set([
  "sS8tOh7vjfVWqx1LmVspxt7w1Bw1",
  "gJ6JhkqHAESiL5tafgE0Xb56S572",
  "Mue0uslkipMUpxMpJUYpF3smVJp2",
  "3hhDyGWx9qdr4kZu1jRXO2dCBji2",
]);
const CEO_AUTH_EMAILS = new Set(["ceo@shineagri.org"]);
const REMOVED = new Set([
  "DELETED",
  "REMOVED",
  "TRASHED",
  "ARCHIVED",
  "SOFT_DELETED",
  "PURGED",
  "CANCELLED",
]);
const FILE_URL_FIELDS = [
  "url",
  "downloadUrl",
  "downloadURL",
  "fileUrl",
  "fileURL",
  "pdfUrl",
  "pdfURL",
  "documentUrl",
  "documentURL",
  "storageUrl",
  "storageURL",
];
const URL_IGNORE_KEYS = new Set([
  "googleMapsUrl",
  "mapUrl",
  "websiteUrl",
  "photoUrl",
  "imageUrl",
  "logoUrl",
]);
const EDIT_FIELDS = [
  ["societyName", "PACS / Society Name"],
  ["registrationNumber", "Registration Number"],
  ["capacityMt", "Capacity (MT)"],
  ["state", "State"],
  ["district", "District"],
  ["taluka", "Taluka / Block"],
  ["village", "Village"],
  ["address", "Full Address"],
  ["contactPerson", "Contact Person"],
  ["mobile", "Mobile"],
  ["email", "Email"],
];
const DOCUMENT_CATEGORIES = [
  ["GENERAL", "General / Supporting"],
  ["APPLICATION", "PACS Application"],
  ["REGISTRATION", "Registration / Bye-laws"],
  ["LAND", "Land / Lease Document"],
  ["FINANCIAL", "Financial Statements / Audit"],
  ["APPROVAL", "Approval / Sanction"],
  ["DPR", "Detailed Project Report (DPR)"],
  ["MOU", "Executed MOU"],
  ["ASSURANCE_LETTER", "NAFED Assurance Letter"],
  ["ESTIMATE", "PMC Estimate / BOQ"],
  ["REGISTRAR", "Registrar File / ZIP"],
  ["OTHER", "Other Official Document"],
];
const DOCUMENT_CATEGORY_KEYS = new Set(DOCUMENT_CATEGORIES.map(([key]) => key));

const clean = (value = "") => String(value ?? "").trim();
const upper = (value = "") => clean(value).toUpperCase();

const hasDirectCeoAccess = (currentUser) => {
  if (!currentUser) return false;
  const uid = clean(currentUser.uid);
  const email = clean(currentUser.email).toLowerCase();
  return CEO_AUTH_UIDS.has(uid) || CEO_AUTH_EMAILS.has(email);
};
const normal = (value = "") => clean(value).toLowerCase().replace(/\s+/g, " ");
const number = (value = 0) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const human = (value = "") =>
  clean(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const safeName = (value = "file") =>
  clean(value).replace(/[^a-zA-Z0-9._-]+/g, "_").slice(-120) || "file";

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (Number.isFinite(Number(value?.seconds))) return Number(value.seconds) * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const dateTime = (value) => {
  const milliseconds = toMillis(value);
  if (!milliseconds) return "—";
  return new Date(milliseconds).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isRemoved = (record = {}) =>
  record?.isDeleted === true ||
  record?.deleted === true ||
  record?.archived === true ||
  record?.isArchived === true ||
  Boolean(record?.deletedAt || record?.archivedAt || record?.removedAt) ||
  [
    record?.status,
    record?.recordStatus,
    record?.registrationStatus,
    record?.applicationStatus,
  ].some((value) => REMOVED.has(upper(value)));

const isNafed = (project = {}) =>
  !isRemoved(project) &&
  (project.nafedProject === true ||
    upper(project.projectSource) === "NAFED" ||
    upper(project.applicationPortal) === "NAFED_PACS_APPLY" ||
    upper(project.portal) === "NAFED" ||
    ["NAFED", "NAFED_PACS_REGISTRATION"].includes(upper(project.registrationPortal)) ||
    upper(project.applicationId || project.projectId).startsWith("NAFED-PACS-"));

const roleCandidates = (record = {}) => {
  const source = record || {};
  const directValues = [
    source.role,
    source.userRole,
    source.accountRole,
    source.portalRole,
    source.authorityRole,
    source.userType,
    source.type,
    source.designation,
    source.department,
  ];
  const arrayValues = [source.roles, source.allowedRoles, source.authorities]
    .filter(Array.isArray)
    .flat();
  return [...directValues, ...arrayValues]
    .map(upper)
    .filter(Boolean);
};

const roleOf = (record = {}) => roleCandidates(record)[0] || "";

const hasCeoDeskAccess = (record = {}) =>
  Boolean(
    record?.admin === true ||
      record?.workflowAdmin === true ||
      record?.shineAgriAdmin === true ||
      roleCandidates(record).some((role) => ALLOWED_ROLES.has(role)),
  );

const projectId = (record = {}) =>
  clean(record.applicationId || record.projectId || record.nafedApplicationId || record.id);

const pacsUid = (project = {}, profile = {}) =>
  clean(
    project.pacsUid ||
      project.pacsUserUid ||
      project.applicantUid ||
      project.submittedByUid ||
      project.userUid ||
      profile.uid ||
      profile.id,
  );

const pacsName = (project = {}, profile = {}) =>
  clean(
    project.societyName ||
      project.pacsName ||
      project.organizationName ||
      project.cooperativeName ||
      profile.societyName ||
      profile.organizationName ||
      profile.pacsName ||
      profile.name ||
      "PACS / Society",
  );

const pmcName = (record = {}) =>
  clean(
    record.organizationName ||
      record.agencyName ||
      record.companyName ||
      record.pmcName ||
      record.name ||
      record.email ||
      "PMC Agency",
  );

const capacity = (project = {}) =>
  Number(
    project.capacityMt ||
      project.capacityMT ||
      project.godownCapacityMT ||
      project.godownCapacity ||
      project.storageCapacityMT ||
      project.capacity ||
      0,
  ) || 0;

const registrationNo = (project = {}, profile = {}) =>
  clean(
    project.registrationNumber ||
      project.registrationNo ||
      project.pacsRegistrationNo ||
      profile.registrationNumber ||
      profile.registrationNo ||
      profile.pacsRegistrationNo,
  );

const locationText = (project = {}) =>
  [
    project.village || project.locationVillage,
    project.taluka || project.block,
    project.district || project.districtName,
    project.state || project.stateName,
  ]
    .map(clean)
    .filter(Boolean)
    .join(", ") || "Location not available";

const identityKeys = (record = {}) => {
  if (!record) return [];
  const mobile = clean(record.mobile || record.phone || record.pacsMobile).replace(/\D/g, "");
  const values = [
    record.id,
    record.uid,
    record.pacsUid,
    record.pacsUserUid,
    record.pacsId,
    record.applicantUid,
    record.submittedByUid,
  ]
    .map(normal)
    .filter(Boolean)
    .map((value) => "uid:" + value);
  const registration = normal(
    record.registrationNumber || record.registrationNo || record.pacsRegistrationNo,
  );
  const email = normal(record.email || record.pacsEmail || record.officialEmail);
  const name = normal(
    record.societyName || record.pacsName || record.organizationName || record.name,
  );
  return Array.from(
    new Set([
      ...values,
      registration && "reg:" + registration,
      email && "email:" + email,
      mobile.length >= 8 && "mobile:" + mobile,
      name && "name:" + name,
    ].filter(Boolean)),
  );
};

const recordsMatch = (left = {}, right = {}) => {
  const rightKeys = new Set(identityKeys(right));
  return identityKeys(left).some((key) => rightKeys.has(key));
};

const urlFrom = (record = {}) => {
  if (!record || typeof record !== "object") return "";
  for (const key of FILE_URL_FIELDS) {
    if (/^https?:\/\//i.test(clean(record[key]))) return clean(record[key]);
  }
  return "";
};

const labelFrom = (record = {}, fallback = "Document") =>
  clean(
    record.title ||
      record.label ||
      record.documentName ||
      record.fileName ||
      record.name ||
      fallback,
  );

function collectFiles(...sources) {
  const files = [];
  const seenObjects = new WeakSet();
  const seenUrls = new Set();

  const add = (url, label, source, meta = {}) => {
    const safeUrl = clean(url);
    if (!/^https?:\/\//i.test(safeUrl) || seenUrls.has(safeUrl)) return;
    seenUrls.add(safeUrl);
    files.push({
      url: safeUrl,
      label: clean(label) || "Document",
      source: clean(source) || "Project file",
      fileName: clean(meta.fileName || meta.name),
      category: upper(meta.category || meta.type || source || "DOCUMENT"),
      uploadedAt: meta.uploadedAt || meta.createdAt || meta.updatedAt || null,
    });
  };

  const walk = (value, key = "Document", source = "Project", depth = 0) => {
    if (depth > 6 || value === null || value === undefined) return;
    if (typeof value === "string") {
      if (!URL_IGNORE_KEYS.has(key) && /^https?:\/\//i.test(value)) {
        add(value, human(key), source);
      }
      return;
    }
    if (typeof value !== "object") return;
    if (seenObjects.has(value)) return;
    seenObjects.add(value);
    if (Array.isArray(value)) {
      value.forEach((item, index) => walk(item, key + " " + (index + 1), source, depth + 1));
      return;
    }
    const directUrl = urlFrom(value);
    if (directUrl) add(directUrl, labelFrom(value, human(key)), source, value);
    Object.entries(value).forEach(([childKey, childValue]) => {
      if (URL_IGNORE_KEYS.has(childKey)) return;
      if (FILE_URL_FIELDS.includes(childKey)) return;
      const nextSource = depth === 0 ? human(childKey) : source;
      walk(childValue, childKey, nextSource, depth + 1);
    });
  };

  sources.filter(Boolean).forEach((source, index) => walk(source, "Document", "Source " + (index + 1)));
  return files.sort((left, right) => toMillis(right.uploadedAt) - toMillis(left.uploadedAt));
}

const firstUrl = (...candidates) => {
  for (const candidate of candidates) {
    if (/^https?:\/\//i.test(clean(candidate))) return clean(candidate);
    const nested = urlFrom(candidate);
    if (nested) return nested;
  }
  return "";
};

const dprUrl = (project = {}, dpr = {}) =>
  firstUrl(
    dpr.finalPacsSignedDprUrl,
    dpr.finalDprUrl,
    dpr.publishedDprUrl,
    dpr.fileUrl,
    dpr.pdfUrl,
    project.finalPacsSignedDprUrl,
    project.finalDprUrl,
    project.publishedDprUrl,
    project.manualDprUrl,
    project.dprUrl,
    project.ceoDprUrl,
    project.manualDpr,
    project.publishedDpr,
    project.dprDocument,
  );

const mouUrl = (project = {}, mou = {}) =>
  firstUrl(
    mou.finalExecutedMouUrl,
    mou.finalMouUrl,
    mou.finalSignedMouUrl,
    mou.fileUrl,
    mou.pdfUrl,
    project.finalExecutedMouUrl,
    project.finalMouUrl,
    project.finalSignedMouUrl,
    project.executedMouUrl,
    project.mouUrl,
    project.ceoMouUrl,
    project.mouDocument,
  );

const isMouExecuted = (project = {}, mou = {}) =>
  Boolean(mouUrl(project, mou)) ||
  project.mouFullyExecuted === true ||
  project.threePartyMouExecuted === true ||
  [
    project.mouStatus,
    project.mouStage,
    project.mouExecutionStatus,
    mou.status,
    mou.mouStatus,
    mou.mouStage,
  ].some((value) =>
    ["FULLY_EXECUTED", "MOU_FULLY_EXECUTED", "MOU_COMPLETED", "EXECUTED"].includes(
      upper(value),
    ),
  );

const approvalStatus = (project = {}) =>
  upper(
    project.ceoReviewStatus ||
      project.headOfficeApprovalStatus ||
      project.branchApprovalStatus ||
      project.applicationStatus ||
      project.status ||
      "PENDING",
  );

const COMPLETED_PROCESS_MARKERS = [
  "APPROVED",
  "ALLOCATED",
  "REGISTERED",
  "SIGNED",
  "EXECUTED",
  "FULLY_EXECUTED",
  "PUBLISHED",
  "COMPLETE",
  "COMPLETED",
  "FORWARDED",
  "VERIFIED",
  "ACCEPTED",
  "CLEARED",
  "SANCTIONED",
];

const processStatus = (candidates = [], completedFallback = false) => {
  for (const candidate of candidates) {
    if (candidate === true) return "COMPLETED";
    if (candidate === false || candidate === null || candidate === undefined) continue;
    if (typeof candidate === "object") {
      const nested = processStatus(
        [candidate.status, candidate.stage, candidate.state, candidate.result],
        false,
      );
      if (nested !== "PENDING") return nested;
      continue;
    }
    const value = upper(candidate);
    if (value) return value;
  }
  return completedFallback ? "COMPLETED" : "PENDING";
};

const isProcessComplete = (status = "") => {
  const value = upper(status);
  return COMPLETED_PROCESS_MARKERS.some(
    (marker) => value === marker || value.includes(marker),
  ) && !["NOT_APPROVED", "NOT_COMPLETED", "NOT_READY", "NOT_PUBLISHED"].some(
    (marker) => value.includes(marker),
  ) && !value.startsWith("NOT ");
};

const isProcessIssue = (status = "") =>
  ["QUERY", "RETURN", "REJECT", "FAILED", "DENIED", "HOLD", "CANCEL"].some(
    (marker) => upper(status).includes(marker),
  );

const statusTone = (status = "") => {
  const value = upper(status);
  if ([...COMPLETED_PROCESS_MARKERS, "ACTIVE", "READY", "SUBMITTED"].some((item) => value.includes(item)) && !value.includes("NOT_") && !value.startsWith("NOT ")) return "success";
  if (["REJECT", "FAILED", "CANCEL", "DENIED"].some((item) => value.includes(item))) return "danger";
  if (["QUERY", "RETURN", "HOLD", "PENDING", "WAIT"].some((item) => value.includes(item))) return "warning";
  return "neutral";
};

const htmlEscape = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character]);

function useLiveCollection(name, enabled = true) {
  const [state, setState] = useState({ rows: [], loading: enabled, error: "" });
  useEffect(() => {
    if (!enabled) {
      setState({ rows: [], loading: false, error: "" });
      return undefined;
    }
    setState((previous) => ({ ...previous, loading: true, error: "" }));
    return onSnapshot(
      collection(db, name),
      (snapshot) => {
        setState({
          rows: snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
          loading: false,
          error: "",
        });
      },
      (error) => {
        console.error("CEO collection listener failed:", name, error);
        setState({ rows: [], loading: false, error: error?.message || "Permission denied" });
      },
    );
  }, [name, enabled]);
  return state;
}

function StatusPill({ value, children }) {
  const text = children || human(value || "Pending");
  return <span className={"ceo-status " + statusTone(value || text)}>{text}</span>;
}

function WorkflowStrip({ steps = [], compact = false }) {
  return (
    <div className={"ceo-workflow-strip" + (compact ? " compact" : "")}>
      {steps.map((step, index) => (
        <div
          key={step.key}
          className={step.done ? "done" : step.issue ? "issue" : "pending"}
          title={`${step.label}: ${human(step.status)}`}
        >
          <i>{step.done ? "✓" : step.issue ? "!" : index + 1}</i>
          <span>{step.shortLabel || step.label}</span>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ title, text }) {
  return (
    <div className="ceo-empty">
      <div className="ceo-empty-mark">NAFED</div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

export default function NafedCeoDashboard() {
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [selectedId, setSelectedId] = useState("");
  const [tab, setTab] = useState("SUMMARY");
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("ALL");
  const [districtFilter, setDistrictFilter] = useState("ALL");
  const [talukaFilter, setTalukaFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [processFilter, setProcessFilter] = useState("ALL");
  const [view, setView] = useState("ALL");
  const [registerMode, setRegisterMode] = useState("APPLICATIONS");
  const [groupBy, setGroupBy] = useState("STATE");
  const [message, setMessage] = useState({ text: "", tone: "success" });
  const [busy, setBusy] = useState("");
  const [remark, setRemark] = useState("");
  const [selectedPmcUid, setSelectedPmcUid] = useState("");
  const [editForm, setEditForm] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("GENERAL");
  const [replaceTarget, setReplaceTarget] = useState(null);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !busy) setSelectedId("");
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedId, busy]);

  useEffect(() => {
    let stopNafedProfile = null;
    let stopPortalProfile = null;
    const stopAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (stopNafedProfile) stopNafedProfile();
      if (stopPortalProfile) stopPortalProfile();
      setUser(currentUser || null);
      setAuthReady(true);
      setProfile(null);
      setProfileReady(!currentUser);
      if (!currentUser?.uid) return;

      try {
        await getIdTokenResult(currentUser, true);
      } catch (error) {
        console.warn("CEO token claims could not be refreshed:", error);
      }

      const resolved = { nafed: false, portal: false };
      const profiles = { nafed: null, portal: null };
      const publishProfile = () => {
        const preferred = hasCeoDeskAccess(profiles.nafed)
          ? profiles.nafed
          : hasCeoDeskAccess(profiles.portal)
            ? profiles.portal
            : profiles.nafed || profiles.portal;
        setProfile(preferred || null);
        if (resolved.nafed && resolved.portal) setProfileReady(true);
      };

      stopNafedProfile = onSnapshot(
        doc(db, COLLECTIONS.users, currentUser.uid),
        (snapshot) => {
          profiles.nafed = snapshot.exists()
            ? { id: snapshot.id, profileSource: COLLECTIONS.users, ...snapshot.data() }
            : null;
          resolved.nafed = true;
          publishProfile();
        },
        (error) => {
          console.error("CEO profile listener failed:", error);
          resolved.nafed = true;
          publishProfile();
        },
      );

      stopPortalProfile = onSnapshot(
        doc(db, COLLECTIONS.portalUsers, currentUser.uid),
        (snapshot) => {
          profiles.portal = snapshot.exists()
            ? { id: snapshot.id, profileSource: COLLECTIONS.portalUsers, ...snapshot.data() }
            : null;
          resolved.portal = true;
          publishProfile();
        },
        (error) => {
          console.error("CEO portal profile listener failed:", error);
          resolved.portal = true;
          publishProfile();
        },
      );
    });
    return () => {
      stopAuth();
      if (stopNafedProfile) stopNafedProfile();
      if (stopPortalProfile) stopPortalProfile();
    };
  }, []);

  const directCeoAccess = hasDirectCeoAccess(user);
  /*
   * Browser profile records and token claims are not the security boundary.
   * Some older CEO Auth accounts have no nafed_users/portal_users profile,
   * which previously caused a false access-restricted screen. Render the desk
   * for an authenticated session and let deployed Firebase rules enforce the
   * actual read/write authorization.
   */
  const authorised = Boolean(user && (directCeoAccess || clean(user.uid)));
  const projectsLive = useLiveCollection(COLLECTIONS.projects, authorised);
  const usersLive = useLiveCollection(COLLECTIONS.users, authorised);
  const portalUsersLive = useLiveCollection(COLLECTIONS.portalUsers, authorised);
  const allocationsLive = useLiveCollection(COLLECTIONS.allocations, authorised);
  const activitiesLive = useLiveCollection(COLLECTIONS.activity, authorised);
  const documentsLive = useLiveCollection(COLLECTIONS.documents, authorised);
  const dprsLive = useLiveCollection(COLLECTIONS.dprs, authorised);
  const mousLive = useLiveCollection(COLLECTIONS.mous, authorised);

  const directoryRows = useMemo(() => {
    const merged = new Map();
    portalUsersLive.rows.forEach((item) => merged.set(item.id, item));
    usersLive.rows.forEach((item) => merged.set(item.id, item));
    return Array.from(merged.values());
  }, [usersLive.rows, portalUsersLive.rows]);

  const pmcs = useMemo(
    () =>
      directoryRows
        .filter((item) =>
          ["PMC", "PMC_AGENCY", "PMC AGENCY"].includes(roleOf(item)),
        )
        .filter((item) => !isRemoved(item) && item.isActive !== false)
        .filter((item) => {
          const values = [
            item.status,
            item.registrationStatus,
            item.roleApprovalStatus,
            item.branchApprovalStatus,
            item.approvalStatus,
          ].map(upper).filter(Boolean);
          if (!values.length) return true;
          if (values.some((value) => ["REJECTED", "BLOCKED", "SUSPENDED", "INACTIVE"].includes(value))) return false;
          return values.some((value) => ["APPROVED", "ACTIVE", "VERIFIED", "ENABLED"].includes(value));
        })
        .sort((left, right) => pmcName(left).localeCompare(pmcName(right))),
    [directoryRows],
  );

  const rows = useMemo(() => {
    const pacsProfiles = directoryRows.filter((item) =>
      ["PACS", "PACS_SOCIETY", "PACS / SOCIETY"].includes(roleOf(item)),
    );
    const activeAllocations = allocationsLive.rows
      .filter((item) => !isRemoved(item))
      .sort(
        (left, right) =>
          toMillis(right.updatedAt || right.allocatedAt || right.createdAt) -
          toMillis(left.updatedAt || left.allocatedAt || left.createdAt),
      );

    return projectsLive.rows
      .filter(isNafed)
      .map((project) => {
        const pacsProfile =
          pacsProfiles.find((item) => recordsMatch(project, item)) || {};
        const allocationRows = activeAllocations.filter(
          (item) =>
            recordsMatch(project, item) ||
            clean(item.projectId) === project.id ||
            clean(item.applicationId) === projectId(project) ||
            (Array.isArray(item.projectIds) && item.projectIds.includes(project.id)),
        );
        const allocation =
          allocationRows.find(
            (item) =>
              recordsMatch(project, item) ||
              clean(item.projectId) === project.id ||
              clean(item.applicationId) === projectId(project) ||
              (Array.isArray(item.projectIds) && item.projectIds.includes(project.id)),
          ) || {};
        const dpr =
          dprsLive.rows.find(
            (item) =>
              item.id === project.id ||
              clean(item.projectId) === project.id ||
              clean(item.applicationId) === projectId(project),
          ) || {};
        const mou =
          mousLive.rows.find(
            (item) =>
              clean(item.projectId) === project.id ||
              clean(item.applicationId) === projectId(project) ||
              recordsMatch(project, item),
          ) || {};
        const relatedDocuments = documentsLive.rows.filter(
          (item) =>
            clean(item.projectId) === project.id ||
            clean(item.applicationId) === projectId(project) ||
            recordsMatch(project, item),
        );
        const documents = collectFiles(project, relatedDocuments, dpr, mou);
        const allocatedPmcUid = clean(
          allocation.allocatedPmcUid ||
            allocation.allocatedPmcId ||
            project.allocatedPmcUid ||
            project.allocatedPmcId ||
            project.pmcUid ||
            project.pmcId,
        );
        const allocatedPmcName = clean(
          allocation.allocatedPmcName ||
            allocation.pmcName ||
            project.allocatedPmcName ||
            project.pmcName,
        );
        const publishedDprUrl = dprUrl(project, dpr);
        const executedMouUrl = mouUrl(project, mou);
        const state = clean(project.state || project.stateName || pacsProfile.state);
        const district = clean(
          project.district || project.districtName || pacsProfile.district,
        );
        const taluka = clean(
          project.taluka ||
            project.block ||
            project.tehsil ||
            project.subDistrict ||
            pacsProfile.taluka ||
            pacsProfile.block ||
            pacsProfile.tehsil,
        );
        const village = clean(
          project.village ||
            project.locationVillage ||
            project.projectVillage ||
            pacsProfile.village ||
            pacsProfile.locationVillage,
        );
        const approval = approvalStatus(project);
        const rawDprStatus = processStatus([
          dpr.status,
          dpr.dprStatus,
          project.dprStatus,
        ]);
        const dprStatusValue = publishedDprUrl && !isProcessComplete(rawDprStatus)
          ? "PUBLISHED"
          : rawDprStatus;
        const mouStatusValue = isMouExecuted(project, mou)
          ? "FULLY_EXECUTED"
          : processStatus([
              project.mouStatus,
              project.mouStage,
              project.mouExecutionStatus,
              mou.mouStatus,
              mou.status,
            ]);
        const districtCompleted = Boolean(
          project.forwardedToStateRegistrar ||
            project.districtRegistrarApproved === true ||
            project.districtRegistrarCompletedAt,
        );
        const rawDistrictRegistrarStatus = processStatus([
            project.districtRegistrarStatus,
            project.districtRegistrationStatus,
            project.registrarDistrictStatus,
            project.districtReviewStatus,
            project.registrarWorkflow?.district,
            project.districtRegistrar,
        ]);
        const districtRegistrarStatus = districtCompleted && !isProcessIssue(rawDistrictRegistrarStatus)
          ? "FORWARDED_TO_STATE"
          : rawDistrictRegistrarStatus;
        const stateCompleted = Boolean(
          project.forwardedToDcdc ||
            project.stateRegistrarApproved === true ||
            project.stateRegistrarCompletedAt,
        );
        const rawStateRegistrarStatus = processStatus([
            project.stateRegistrarStatus,
            project.stateRegistrationStatus,
            project.registrarStateStatus,
            project.stateReviewStatus,
            project.registrarWorkflow?.state,
            project.stateRegistrar,
        ]);
        const stateRegistrarStatus = stateCompleted && !isProcessIssue(rawStateRegistrarStatus)
          ? "FORWARDED_TO_DCDC"
          : rawStateRegistrarStatus;
        const dcdcCompleted = Boolean(
          project.dcdcApproved === true || project.dcdcApprovedAt,
        );
        const rawDcdcStatus = processStatus([
            project.dcdcStatus,
            project.dcdcApprovalStatus,
            project.dcdcWorkflowStatus,
            project.districtCooperativeDevelopmentCommitteeStatus,
            project.dcdc,
        ]);
        const dcdcStatus = dcdcCompleted && !isProcessIssue(rawDcdcStatus)
          ? "APPROVED"
          : rawDcdcStatus;
        const rawRegistrationStatus = processStatus([
            project.registrationStatus,
            project.applicationStatus,
            project.submissionStatus,
        ]);
        const registrationStatus = projectId(project) && !isProcessIssue(rawRegistrationStatus)
          ? "REGISTERED"
          : rawRegistrationStatus;
        const processSteps = [
          { key: "REGISTRATION", label: "Registration", shortLabel: "REG", status: registrationStatus },
          { key: "CEO_REVIEW", label: "CEO Review", shortLabel: "CEO", status: approval },
          { key: "PMC_ALLOTMENT", label: "PMC Allotment", shortLabel: "PMC", status: allocatedPmcUid || allocatedPmcName ? "ALLOCATED" : "PENDING" },
          { key: "MOU", label: "MOU Execution", shortLabel: "MOU", status: mouStatusValue },
          { key: "DPR", label: "Final DPR", shortLabel: "DPR", status: dprStatusValue },
          { key: "DISTRICT_REGISTRAR", label: "District Registrar", shortLabel: "DR", status: districtRegistrarStatus },
          { key: "STATE_REGISTRAR", label: "State Registrar", shortLabel: "SR", status: stateRegistrarStatus },
          { key: "DCDC", label: "DCDC", shortLabel: "DCDC", status: dcdcStatus },
        ].map((step) => ({
          ...step,
          done: isProcessComplete(step.status),
          issue: isProcessIssue(step.status),
        }));
        const completedSteps = processSteps.filter((step) => step.done).length;
        const currentStep =
          processSteps.find((step) => !step.done) ||
          { key: "COMPLETE", label: "Workflow Complete", status: "COMPLETED" };
        const submittedAt =
          project.submittedAt || project.applicationSubmittedAt || project.createdAt;
        const updatedAt = project.updatedAt || project.modifiedAt || submittedAt;
        return {
          id: project.id,
          project,
          pacsProfile,
          allocation,
          allocationRows,
          dpr,
          mou,
          relatedDocuments,
          documents,
          applicationId: projectId(project),
          pacsUid: pacsUid(project, pacsProfile),
          pacsName: pacsName(project, pacsProfile),
          registrationNo: registrationNo(project, pacsProfile),
          capacityMt: capacity(project),
          state,
          district,
          taluka,
          village,
          location: locationText(project),
          approval,
          allocatedPmcUid,
          allocatedPmcName,
          dprUrl: publishedDprUrl,
          dprStatus: publishedDprUrl ? dprStatusValue || "PUBLISHED" : dprStatusValue,
          mouUrl: executedMouUrl,
          mouExecuted: isMouExecuted(project, mou),
          mouStatus: mouStatusValue,
          districtRegistrarStatus,
          stateRegistrarStatus,
          dcdcStatus,
          processSteps,
          workflowProgress: Math.round((completedSteps / processSteps.length) * 100),
          currentStageKey: currentStep.key,
          currentStage: currentStep.label,
          submittedAt,
          updatedAt,
        };
      })
      .sort((left, right) => toMillis(right.updatedAt) - toMillis(left.updatedAt));
  }, [
    projectsLive.rows,
    directoryRows,
    allocationsLive.rows,
    documentsLive.rows,
    dprsLive.rows,
    mousLive.rows,
  ]);

  const states = useMemo(
    () => Array.from(new Set(rows.map((item) => item.state).filter(Boolean))).sort(),
    [rows],
  );

  const districts = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .filter((item) => stateFilter === "ALL" || item.state === stateFilter)
            .map((item) => item.district)
            .filter(Boolean),
        ),
      ).sort(),
    [rows, stateFilter],
  );

  const talukas = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .filter(
              (item) =>
                (stateFilter === "ALL" || item.state === stateFilter) &&
                (districtFilter === "ALL" || item.district === districtFilter),
            )
            .map((item) => item.taluka)
            .filter(Boolean),
        ),
      ).sort(),
    [rows, stateFilter, districtFilter],
  );

  const processStages = useMemo(
    () =>
      Array.from(
        new Map(
          rows.map((item) => [item.currentStageKey, item.currentStage]),
        ).entries(),
      ),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const needle = normal(search);
    return rows.filter((item) => {
      const searchMatch = !needle || normal([
        item.pacsName,
        item.applicationId,
        item.registrationNo,
        item.state,
        item.district,
        item.taluka,
        item.village,
        item.location,
        item.allocatedPmcName,
        item.currentStage,
      ].join(" ")).includes(needle);
      const stateMatch = stateFilter === "ALL" || item.state === stateFilter;
      const districtMatch =
        districtFilter === "ALL" || item.district === districtFilter;
      const talukaMatch = talukaFilter === "ALL" || item.taluka === talukaFilter;
      const statusMatch = statusFilter === "ALL" || item.approval === statusFilter;
      const processMatch =
        processFilter === "ALL" || item.currentStageKey === processFilter;
      const viewMatch =
        view === "ALL" ||
        (view === "REVIEW" && !["APPROVED", "REJECTED"].includes(item.approval)) ||
        (view === "APPROVED" && item.approval === "APPROVED") ||
        (view === "ALLOCATED" && Boolean(item.allocatedPmcUid || item.allocatedPmcName)) ||
        (view === "UNALLOCATED" && !item.allocatedPmcUid && !item.allocatedPmcName) ||
        (view === "DPR" && Boolean(item.dprUrl)) ||
        (view === "MOU" && item.mouExecuted) ||
        (view === "DISTRICT" && isProcessComplete(item.districtRegistrarStatus)) ||
        (view === "STATE" && isProcessComplete(item.stateRegistrarStatus)) ||
        (view === "DCDC" && isProcessComplete(item.dcdcStatus)) ||
        (view === "COMPLETE" && item.currentStageKey === "COMPLETE");
      return (
        searchMatch &&
        stateMatch &&
        districtMatch &&
        talukaMatch &&
        statusMatch &&
        processMatch &&
        viewMatch
      );
    });
  }, [
    rows,
    search,
    stateFilter,
    districtFilter,
    talukaFilter,
    statusFilter,
    processFilter,
    view,
  ]);

  const metrics = useMemo(
    () => ({
      total: filteredRows.length,
      capacity: filteredRows.reduce((sum, item) => sum + item.capacityMt, 0),
      approved: filteredRows.filter((item) => item.approval === "APPROVED").length,
      allocated: filteredRows.filter(
        (item) => item.allocatedPmcUid || item.allocatedPmcName,
      ).length,
      dpr: filteredRows.filter((item) => item.dprUrl).length,
      mou: filteredRows.filter((item) => item.mouExecuted).length,
      district: filteredRows.filter((item) =>
        isProcessComplete(item.districtRegistrarStatus),
      ).length,
      complete: filteredRows.filter((item) => item.currentStageKey === "COMPLETE").length,
    }),
    [filteredRows],
  );

  const geographyReport = useMemo(() => {
    const grouped = new Map();
    filteredRows.forEach((item) => {
      const state = item.state || "Not Recorded";
      const district = item.district || "Not Recorded";
      const taluka = item.taluka || "Not Recorded";
      const key =
        groupBy === "STATE"
          ? state
          : groupBy === "DISTRICT"
            ? `${state}||${district}`
            : `${state}||${district}||${taluka}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          state,
          district: groupBy === "STATE" ? "All Districts" : district,
          taluka: groupBy === "TALUKA" ? taluka : "All Talukas",
          applications: 0,
          capacityMt: 0,
          approved: 0,
          allocated: 0,
          mou: 0,
          dpr: 0,
          districtReady: 0,
          stateReady: 0,
          dcdcReady: 0,
          complete: 0,
          documents: 0,
          progressTotal: 0,
        });
      }
      const report = grouped.get(key);
      report.applications += 1;
      report.capacityMt += item.capacityMt;
      report.approved += item.approval === "APPROVED" ? 1 : 0;
      report.allocated += item.allocatedPmcUid || item.allocatedPmcName ? 1 : 0;
      report.mou += item.mouExecuted ? 1 : 0;
      report.dpr += item.dprUrl ? 1 : 0;
      report.districtReady += isProcessComplete(item.districtRegistrarStatus) ? 1 : 0;
      report.stateReady += isProcessComplete(item.stateRegistrarStatus) ? 1 : 0;
      report.dcdcReady += isProcessComplete(item.dcdcStatus) ? 1 : 0;
      report.complete += item.currentStageKey === "COMPLETE" ? 1 : 0;
      report.documents += item.documents.length;
      report.progressTotal += item.workflowProgress;
    });
    return Array.from(grouped.values())
      .map((item) => ({
        ...item,
        averageProgress: item.applications
          ? Math.round(item.progressTotal / item.applications)
          : 0,
      }))
      .sort((left, right) =>
        [left.state, left.district, left.taluka]
          .join("|")
          .localeCompare([right.state, right.district, right.taluka].join("|")),
      );
  }, [filteredRows, groupBy]);

  const selected = rows.find((item) => item.id === selectedId) || null;
  const selectedActivities = useMemo(() => {
    if (!selected) return [];
    return activitiesLive.rows
      .filter(
        (item) =>
          clean(item.projectId) === selected.id ||
          clean(item.applicationId) === selected.applicationId ||
          clean(item.targetUid) === selected.pacsUid,
      )
      .sort((left, right) => toMillis(right.createdAt) - toMillis(left.createdAt));
  }, [selected, activitiesLive.rows]);

  useEffect(() => {
    if (!selected) return;
    const source = selected.project;
    setSelectedPmcUid(selected.allocatedPmcUid || "");
    setRemark(clean(source.ceoRemarks || source.headOfficeRemarks));
    setEditForm({
      societyName: selected.pacsName,
      registrationNumber: selected.registrationNo,
      capacityMt: selected.capacityMt || "",
      state: selected.state,
      district: selected.district,
      taluka: clean(source.taluka || source.block),
      village: clean(source.village || source.locationVillage),
      address: clean(source.address || source.fullAddress),
      contactPerson: clean(source.contactPerson || source.authorisedPersonName),
      mobile: clean(source.mobile || source.phone || selected.pacsProfile.mobile),
      email: clean(source.email || selected.pacsProfile.email),
    });
    setUploadFile(null);
    setUploadTitle("");
    setUploadCategory("GENERAL");
    setReplaceTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [selected?.id]);

  const loading =
    projectsLive.loading ||
    usersLive.loading ||
    portalUsersLive.loading ||
    allocationsLive.loading ||
    dprsLive.loading ||
    mousLive.loading;
  const listenerErrors = [
    ["Applications", projectsLive.error],
    ["PACS / PMC Directory", usersLive.error],
    ["Legacy Portal Directory", portalUsersLive.error],
    ["PMC Allocations", allocationsLive.error],
    ["Audit Trail", activitiesLive.error],
    ["Documents", documentsLive.error],
    ["DPR Register", dprsLive.error],
    ["MOU Register", mousLive.error],
  ].filter(([, error]) => Boolean(error));

  const notify = (text, tone = "success") => {
    setMessage({ text, tone });
    window.clearTimeout(notify.timer);
    notify.timer = window.setTimeout(() => setMessage({ text: "", tone: "success" }), 6000);
  };

  const saveAudit = async (action, row, extra = {}) => {
    try {
      await addDoc(collection(db, COLLECTIONS.activity), {
        action,
        projectId: row.id,
        applicationId: row.applicationId,
        targetUid: row.pacsUid,
        targetRole: "PACS",
        targetName: row.pacsName,
        state: row.state,
        district: row.district,
        performedByUid: user?.uid || "",
        performedByRole: "HEAD_OFFICE",
        performedByName: clean(profile?.name || profile?.fullName || profile?.email),
        createdAt: serverTimestamp(),
        ...extra,
      });
    } catch (error) {
      console.warn("CEO audit could not be written:", error);
    }
  };

  const run = async (key, task) => {
    if (!selected || busy) return;
    try {
      setBusy(key);
      await task();
    } catch (error) {
      console.error("CEO action failed:", error);
      notify(
        error?.code === "permission-denied"
          ? "Permission denied. Included CEO Firebase rules add-on deploy karein."
          : error?.message || "Action complete nahi ho saka.",
        "danger",
      );
    } finally {
      setBusy("");
    }
  };

  const saveDecision = (decision) =>
    run("decision", async () => {
      if (decision !== "APPROVED" && !clean(remark)) {
        throw new Error("Query / hold / rejection ke liye remarks mandatory hain.");
      }
      await updateDoc(doc(db, COLLECTIONS.projects, selected.id), {
        ceoReviewStatus: decision,
        headOfficeApprovalStatus: decision,
        ceoRemarks: clean(remark),
        headOfficeRemarks: clean(remark),
        ceoReviewedByUid: user.uid,
        ceoReviewedByName: clean(profile?.name || profile?.fullName || profile?.email),
        ceoReviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await saveAudit("CEO_APPLICATION_" + decision, selected, {
        decision,
        remarks: clean(remark),
      });
      notify("CEO decision saved: " + human(decision) + ".");
    });

  const saveAllocation = () =>
    run("allocation", async () => {
      const pmc = pmcs.find((item) => item.id === selectedPmcUid);
      if (!pmc) throw new Error("Approved PMC select karein.");
      const previousPmcUid = clean(selected.allocatedPmcUid);
      const previousPmcName = clean(selected.allocatedPmcName);
      const isReallocation = Boolean(previousPmcUid && previousPmcUid !== pmc.id);
      if (
        isReallocation &&
        !window.confirm(
          `${previousPmcName || "Current PMC"} se allocation ${pmcName(pmc)} ko transfer karna hai? Existing application aur documents safe rahenge.`,
        )
      ) return;
      const linkedAllocationIds = Array.from(
        new Set(
          (selected.allocationRows || [])
            .map((item) => clean(item.id))
            .filter(Boolean),
        ),
      );
      const primaryAllocationId =
        clean(selected.allocation.id) || selected.pacsUid || selected.id;
      if (!linkedAllocationIds.length) linkedAllocationIds.push(primaryAllocationId);
      const currentProjects = Array.isArray(selected.allocation.projectIds)
        ? selected.allocation.projectIds
        : [];
      const historyEntry = {
        action: isReallocation ? "REALLOCATED" : "ALLOCATED",
        fromPmcUid: previousPmcUid,
        fromPmcName: previousPmcName,
        toPmcUid: pmc.id,
        toPmcName: pmcName(pmc),
        changedByUid: user.uid,
        changedByRole: "HEAD_OFFICE",
        changedAt: new Date().toISOString(),
      };
      const allocationFields = {
        pacsUid: selected.pacsUid || primaryAllocationId,
        pacsId: selected.pacsUid || primaryAllocationId,
        pacsUserUid: selected.pacsUid || primaryAllocationId,
        pacsName: selected.pacsName,
        societyName: selected.pacsName,
        registrationNumber: selected.registrationNo,
        pacsRegistrationNo: selected.registrationNo,
        applicationId: selected.applicationId,
        projectId: selected.id,
        projectIds: Array.from(new Set([...currentProjects, selected.id])),
        state: selected.state,
        district: selected.district,
        allocatedPmcUid: pmc.id,
        allocatedPmcId: pmc.id,
        allocatedPmcName: pmcName(pmc),
        allocatedPmcEmail: clean(pmc.email || pmc.officialEmail),
        allocatedPmcMobile: clean(pmc.mobile || pmc.phone),
        pmcUid: pmc.id,
        pmcId: pmc.id,
        pmcName: pmcName(pmc),
        allocationStatus: "ACTIVE",
        allocationMode: "PACS_WISE",
        status: "ACTIVE",
        isActive: true,
        isDeleted: false,
        allocatedByUid: user.uid,
        allocatedByRole: "HEAD_OFFICE",
        createdAt: selected.allocation.createdAt || serverTimestamp(),
        allocatedAt: serverTimestamp(),
        reallocatedAt: isReallocation ? serverTimestamp() : selected.allocation.reallocatedAt || null,
        previousPmcUid,
        previousPmcName,
        allocationHistory: arrayUnion(historyEntry),
        updatedAt: serverTimestamp(),
      };
      const batch = writeBatch(db);
      linkedAllocationIds.forEach((allocationId) => {
        batch.set(doc(db, COLLECTIONS.allocations, allocationId), allocationFields, {
          merge: true,
        });
      });
      batch.set(
        doc(db, COLLECTIONS.projects, selected.id),
        {
          allocatedPmcUid: pmc.id,
          allocatedPmcId: pmc.id,
          allocatedPmcName: pmcName(pmc),
          allocatedPmcEmail: clean(pmc.email || pmc.officialEmail),
          allocatedPmcMobile: clean(pmc.mobile || pmc.phone),
          pmcUid: pmc.id,
          pmcId: pmc.id,
          pmcName: pmcName(pmc),
          allocationStatus: "ACTIVE",
          approvalAllocationId: primaryAllocationId,
          allocatedByUid: user.uid,
          allocatedByRole: "HEAD_OFFICE",
          allocatedAt: serverTimestamp(),
          reallocatedAt: isReallocation ? serverTimestamp() : selected.project.reallocatedAt || null,
          previousPmcUid,
          previousPmcName,
          pmcAllocationHistory: arrayUnion(historyEntry),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await batch.commit();
      await saveAudit(isReallocation ? "CEO_PMC_REALLOCATED" : "CEO_PACS_ALLOCATED_TO_PMC", selected, {
        previousPmcUid,
        previousPmcName,
        allocatedPmcUid: pmc.id,
        allocatedPmcName: pmcName(pmc),
      });
      notify(
        selected.pacsName +
          (isReallocation ? " successfully re-allotted to " : " successfully allotted to ") +
          pmcName(pmc) +
          ". All linked allocation records synced.",
      );
    });

  const clearAllocation = () =>
    run("allocation", async () => {
      if (!window.confirm("Current PMC allocation clear karni hai? Project delete nahi hoga.")) return;
      const linkedAllocationIds = Array.from(
        new Set(
          (selected.allocationRows || [])
            .map((item) => clean(item.id))
            .filter(Boolean),
        ),
      );
      const primaryAllocationId =
        clean(selected.allocation.id) || selected.pacsUid || selected.id;
      if (!linkedAllocationIds.length) linkedAllocationIds.push(primaryAllocationId);
      const commonCleared = {
        allocatedPmcUid: "",
        allocatedPmcId: "",
        allocatedPmcName: "",
        allocatedPmcEmail: "",
        allocatedPmcMobile: "",
        pmcUid: "",
        pmcId: "",
        pmcName: "",
        allocationStatus: "UNALLOCATED",
        allocationRemovedByUid: user.uid,
        allocationRemovedByRole: "HEAD_OFFICE",
        allocationRemovedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const batch = writeBatch(db);
      linkedAllocationIds.forEach((allocationId) => {
        batch.set(
          doc(db, COLLECTIONS.allocations, allocationId),
          { ...commonCleared, status: "UNALLOCATED", isActive: false },
          { merge: true },
        );
      });
      batch.set(doc(db, COLLECTIONS.projects, selected.id), commonCleared, {
        merge: true,
      });
      await batch.commit();
      await saveAudit("CEO_PMC_ALLOCATION_CLEARED", selected);
      setSelectedPmcUid("");
      notify("PMC allocation cleared. Application and files safe hain.", "warning");
    });

  const saveApplication = () =>
    run("edit", async () => {
      const numericCapacity = Number(String(editForm.capacityMt || "").replace(/,/g, ""));
      if (!clean(editForm.societyName)) throw new Error("PACS name mandatory hai.");
      if (editForm.capacityMt !== "" && (!Number.isFinite(numericCapacity) || numericCapacity < 0)) {
        throw new Error("Capacity valid number honi chahiye.");
      }
      const payload = {
        societyName: clean(editForm.societyName),
        pacsName: clean(editForm.societyName),
        registrationNumber: clean(editForm.registrationNumber),
        capacityMt: numericCapacity || 0,
        state: clean(editForm.state),
        district: clean(editForm.district),
        taluka: clean(editForm.taluka),
        village: clean(editForm.village),
        address: clean(editForm.address),
        contactPerson: clean(editForm.contactPerson),
        mobile: clean(editForm.mobile),
        email: clean(editForm.email),
        ceoLastEditedByUid: user.uid,
        ceoLastEditedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, COLLECTIONS.projects, selected.id), payload);
      await saveAudit("CEO_APPLICATION_DETAILS_UPDATED", selected, {
        changedFields: EDIT_FIELDS.map(([key]) => key),
      });
      notify("Application details safely updated. Workflow status unchanged hai.");
    });

  const prepareDocumentVersion = (file) => {
    const requestedCategory = upper(file?.category || "GENERAL");
    setReplaceTarget(file);
    setUploadTitle(clean(file?.label || file?.fileName || "Updated Document"));
    setUploadCategory(
      DOCUMENT_CATEGORY_KEYS.has(requestedCategory) ? requestedCategory : "GENERAL",
    );
    setUploadFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    window.setTimeout(() => fileInputRef.current?.click(), 50);
  };

  const uploadDocument = () =>
    run("upload", async () => {
      if (!uploadFile) throw new Error("Upload ke liye file select karein.");
      const allowed = [
        "application/pdf",
        "image/png",
        "image/jpeg",
        "image/webp",
        "application/zip",
      ];
      if (!allowed.includes(uploadFile.type)) {
        throw new Error("Only PDF, PNG, JPG, WEBP or ZIP file allowed hai.");
      }
      if (uploadFile.size > 50 * 1024 * 1024) {
        throw new Error("File 50 MB se chhoti honi chahiye.");
      }
      const category = upper(uploadCategory || "GENERAL");
      const storagePath = [
        "nafed_ceo",
        user.uid,
        selected.id,
        category.toLowerCase(),
        Date.now() + "_" + safeName(uploadFile.name),
      ].join("/");
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, uploadFile, {
        contentType: uploadFile.type,
        customMetadata: {
          projectId: selected.id,
          applicationId: selected.applicationId,
          category,
          uploadedByUid: user.uid,
          uploadedByRole: "HEAD_OFFICE",
        },
      });
      const url = await getDownloadURL(storageRef);
      const record = {
        id: "CEO-" + Date.now(),
        title: clean(uploadTitle) || human(category) + " Document",
        fileName: uploadFile.name,
        contentType: uploadFile.type,
        size: uploadFile.size,
        category,
        url,
        storagePath,
        uploadedByUid: user.uid,
        uploadedByRole: "HEAD_OFFICE",
        uploadedAt: new Date().toISOString(),
        versionMode: replaceTarget ? "NEW_VERSION" : "NEW_DOCUMENT",
        replacesUrl: clean(replaceTarget?.url),
        replacesLabel: clean(replaceTarget?.label),
        previousSource: clean(replaceTarget?.source),
      };
      const projectUpdate = {
        ceoDocuments: arrayUnion(record),
        ceoLastUploadedDocument: record,
        updatedAt: serverTimestamp(),
      };
      if (category === "DPR") {
        projectUpdate.ceoDprUrl = url;
        projectUpdate.ceoDprDocument = record;
      }
      if (category === "MOU") {
        projectUpdate.ceoMouUrl = url;
        projectUpdate.ceoMouDocument = record;
      }
      await updateDoc(doc(db, COLLECTIONS.projects, selected.id), projectUpdate);
      await saveAudit(
        replaceTarget ? "CEO_PROJECT_DOCUMENT_REUPLOADED" : "CEO_PROJECT_DOCUMENT_UPLOADED",
        selected,
        {
        documentCategory: category,
        documentTitle: record.title,
        documentUrl: url,
        previousDocumentUrl: clean(replaceTarget?.url),
        },
      );
      setUploadFile(null);
      setUploadTitle("");
      setReplaceTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      notify(
        record.title +
          (record.versionMode === "NEW_VERSION"
            ? " ka new version uploaded successfully. Old version history me safe hai."
            : " uploaded successfully."),
      );
    });

  const printApplication = (row = selected) => {
    if (!row) return;
    const windowRef = window.open("", "_blank", "width=1000,height=900,scrollbars=yes");
    if (!windowRef) {
      notify("Pop-up blocked. Browser me pop-ups allow karein.", "danger");
      return;
    }
    const fields = [
      ["Application ID", row.applicationId],
      ["PACS / Society", row.pacsName],
      ["Registration No.", row.registrationNo || "—"],
      ["Location", row.location],
      ["State", row.state || "—"],
      ["District", row.district || "—"],
      ["Taluka / Block", row.taluka || "—"],
      ["Village", row.village || "—"],
      ["Capacity", number(row.capacityMt) + " MT"],
      ["CEO Review", human(row.approval)],
      ["Allocated PMC", row.allocatedPmcName || "Not allotted"],
      ["DPR", row.dprUrl ? "Available" : "Not published"],
      ["MOU", row.mouExecuted ? "Fully executed" : human(row.mouStatus)],
      ["District Registrar", human(row.districtRegistrarStatus)],
      ["State Registrar", human(row.stateRegistrarStatus)],
      ["DCDC", human(row.dcdcStatus)],
      ["Current Process", row.currentStage],
      ["Workflow Progress", row.workflowProgress + "%"],
      ["Submitted", dateTime(row.submittedAt)],
      ["Last Updated", dateTime(row.updatedAt)],
    ];
    windowRef.document.open();
    windowRef.document.write(
      "<!doctype html><html><head><meta charset='utf-8'><title>" +
        htmlEscape(row.applicationId) +
        "</title><style>@page{size:A4;margin:16mm}body{font-family:Arial,sans-serif;color:#142c4f;margin:0}header{border-top:8px solid #ef8b24;border-bottom:4px solid #1f7b49;padding:18px 0}h1{font-size:23px;margin:0}h2{font-size:16px;margin:6px 0 0;color:#1f7b49}.meta{margin:22px 0;padding:18px;border:1px solid #c9d5e4;background:#f6f9fc}table{width:100%;border-collapse:collapse}th,td{padding:12px;border:1px solid #c9d5e4;text-align:left;font-size:12px}th{width:30%;background:#edf3f9}.foot{margin-top:25px;border-top:1px solid #ccd6e2;padding-top:10px;font-size:10px;color:#63758a}</style></head><body><header><h1>NAFED CEO Application Control Sheet</h1><h2>National Agricultural Cooperative Marketing Federation of India Ltd.</h2></header><div class='meta'><b>Official NAFED PACS Project File</b><br>Live system record generated on " +
        htmlEscape(new Date().toLocaleString("en-IN")) +
        "</div><table>" +
        fields
          .map(
            ([label, value]) =>
              "<tr><th>" + htmlEscape(label) + "</th><td>" + htmlEscape(value) + "</td></tr>",
          )
          .join("") +
        "</table><div class='foot'>System-generated control sheet. Source documents remain authoritative.</div><script>window.onload=function(){setTimeout(function(){window.print()},300)};<\/script></body></html>",
    );
    windowRef.document.close();
  };

  const exportCsv = () => {
    const headings = [
      "Application ID",
      "PACS",
      "Registration No",
      "State",
      "District",
      "Taluka / Block",
      "Village",
      "Capacity MT",
      "CEO Review",
      "Allocated PMC",
      "Documents",
      "DPR",
      "MOU",
      "District Registrar",
      "State Registrar",
      "DCDC",
      "Current Stage",
      "Workflow Progress %",
      "Submitted",
      "Last Updated",
    ];
    const quote = (value) => '"' + String(value ?? "").replace(/"/g, '""') + '"';
    const lines = [
      headings.map(quote).join(","),
      ...filteredRows.map((row) =>
        [
          row.applicationId,
          row.pacsName,
          row.registrationNo,
          row.state,
          row.district,
          row.taluka,
          row.village,
          row.capacityMt,
          row.approval,
          row.allocatedPmcName,
          row.documents.length,
          row.dprUrl ? "AVAILABLE" : "NOT_PUBLISHED",
          row.mouExecuted ? "FULLY_EXECUTED" : row.mouStatus,
          row.districtRegistrarStatus,
          row.stateRegistrarStatus,
          row.dcdcStatus,
          row.currentStage,
          row.workflowProgress,
          dateTime(row.submittedAt),
          dateTime(row.updatedAt),
        ].map(quote).join(","),
      ),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "NAFED_CEO_Application_Register_" + new Date().toISOString().slice(0, 10) + ".csv";
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const exportGeographyCsv = () => {
    const headings = [
      "State",
      "District",
      "Taluka / Block",
      "Applications",
      "Capacity MT",
      "CEO Approved",
      "PMC Allotted",
      "MOU Executed",
      "DPR Available",
      "District Registrar",
      "State Registrar",
      "DCDC",
      "Workflow Complete",
      "Documents",
      "Average Progress %",
    ];
    const quote = (value) => '"' + String(value ?? "").replace(/"/g, '""') + '"';
    const lines = [
      headings.map(quote).join(","),
      ...geographyReport.map((row) =>
        [
          row.state,
          row.district,
          row.taluka,
          row.applications,
          row.capacityMt,
          row.approved,
          row.allocated,
          row.mou,
          row.dpr,
          row.districtReady,
          row.stateReady,
          row.dcdcReady,
          row.complete,
          row.documents,
          row.averageProgress,
        ].map(quote).join(","),
      ),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      `NAFED_CEO_${groupBy}_Wise_Report_` +
      new Date().toISOString().slice(0, 10) +
      ".csv";
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openControl = (row, nextTab = "SUMMARY") => {
    setSelectedId(row.id);
    setTab(nextTab);
  };

  if (!authReady || (user && !directCeoAccess && !profileReady)) {
    return <div className="ceo-screen-message">Secure CEO desk is loading…</div>;
  }

  if (!user) {
    return (
      <div className="ceo-screen-message">
        <h2>Session expired</h2>
        <p>Please sign in again through the NAFED portal.</p>
      </div>
    );
  }

  return (
    <div className="ceo-app">
      <div className="ceo-tricolour" />
      <header className="ceo-header">
        <div className="ceo-brand">
          <div className="ceo-logo-wrap">
            <img src="/images/nafed-logo.png" alt="NAFED" onError={(event) => { event.currentTarget.style.display = "none"; }} />
            <span>NAFED</span>
          </div>
          <div>
            <p>National Agricultural Cooperative Marketing Federation of India Ltd.</p>
            <h1>CEO National Control Desk</h1>
            <small>NAFED PACS Grain Storage Programme · All-in-One Live Governance</small>
          </div>
        </div>
        <div className="ceo-head-right">
          <div className="ceo-live"><i /> LIVE FIRESTORE</div>
          <div className="ceo-clock">
            <b>{clock.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</b>
            <span>{clock.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
          </div>
          <div className="ceo-user">
            <span>{clean(profile?.name || profile?.fullName || "CEO").slice(0, 1).toUpperCase()}</span>
            <div><b>{clean(profile?.name || profile?.fullName || profile?.email || "CEO")}</b><small>{human(roleOf(profile) || "CEO")}</small></div>
          </div>
          <button className="ceo-btn ghost" type="button" onClick={() => signOut(auth)}>Logout</button>
        </div>
      </header>

      <main className="ceo-main">
        <section className="ceo-hero">
          <div>
            <span className="ceo-eyebrow">NATIONAL APPLICATION COMMAND CENTRE</span>
            <h2>Every NAFED Registration. One Control Desk.</h2>
            <p>Review applications, approve decisions, allot approved PMCs, edit particulars, upload/download documents and access DPR & MOU without switching portals.</p>
          </div>
          <div className="ceo-hero-actions">
            <button className="ceo-btn light" type="button" onClick={exportCsv}>Export Live Register</button>
            <button className="ceo-btn light" type="button" onClick={() => window.print()}>Print Dashboard</button>
          </div>
        </section>

        {message.text && <div className={"ceo-alert " + message.tone}>{message.text}<button type="button" onClick={() => setMessage({ text: "", tone: "success" })}>×</button></div>}
        {listenerErrors.length > 0 && (
          <div className="ceo-alert danger ceo-access-alert">
            <div>
              <b>Live access rules required</b>
              <span>{listenerErrors.map(([name]) => name).join(", ")} could not be read for this CEO login. Deploy the included complete Firestore and Storage rules; existing records remain unchanged.</span>
            </div>
          </div>
        )}

        <section className="ceo-metrics">
          {[
            ["TOTAL", "Applications", metrics.total, "Current report scope"],
            ["CAPACITY", "Storage Capacity", number(metrics.capacity) + " MT", "Filtered project capacity"],
            ["APPROVED", "CEO Approved", metrics.approved, "Approved applications"],
            ["ALLOCATED", "PMC Allotted", metrics.allocated, "Active allocations"],
            ["MOU", "MOU Executed", metrics.mou, "Signed agreements"],
            ["DPR", "DPR Available", metrics.dpr, "Published project reports"],
            ["DISTRICT", "District Cleared", metrics.district, "Registrar processing"],
            ["COMPLETE", "Workflow Complete", metrics.complete, "Through DCDC stage"],
          ].map(([key, label, value, note]) => (
            <button key={key} className={"ceo-metric " + (view === key ? "active" : "")} type="button" onClick={() => setView(["TOTAL", "CAPACITY"].includes(key) ? "ALL" : key)}>
              <span>{label}</span><b>{value}</b><small>{note}</small>
            </button>
          ))}
        </section>

        <section className="ceo-register">
          <div className="ceo-register-head">
            <div><span className="ceo-eyebrow">LIVE NATIONAL MIS & CONTROL REGISTER</span><h2>NAFED PACS Applications & Process Reports</h2><p>State, district and taluka-wise live reporting with complete application workflow.</p></div>
            <div className="ceo-register-tools">
              <div className="ceo-register-switch" role="tablist" aria-label="Register view">
                <button className={registerMode === "APPLICATIONS" ? "active" : ""} type="button" onClick={() => setRegisterMode("APPLICATIONS")}>Application Register</button>
                <button className={registerMode === "GEOGRAPHY" ? "active" : ""} type="button" onClick={() => setRegisterMode("GEOGRAPHY")}>Geographical Report</button>
              </div>
              <div className="ceo-result-count"><b>{registerMode === "GEOGRAPHY" ? geographyReport.length : filteredRows.length}</b><span>{registerMode === "GEOGRAPHY" ? "report rows" : "records shown"}</span></div>
            </div>
          </div>
          <div className="ceo-filters">
            <label className="ceo-search"><span>Search</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="PACS, Application ID, registration, district or PMC…" /></label>
            <label><span>State</span><select value={stateFilter} onChange={(event) => { setStateFilter(event.target.value); setDistrictFilter("ALL"); setTalukaFilter("ALL"); }}><option value="ALL">All States</option>{states.map((state) => <option key={state} value={state}>{state}</option>)}</select></label>
            <label><span>District</span><select value={districtFilter} onChange={(event) => { setDistrictFilter(event.target.value); setTalukaFilter("ALL"); }}><option value="ALL">All Districts</option>{districts.map((district) => <option key={district} value={district}>{district}</option>)}</select></label>
            <label><span>Taluka / Block</span><select value={talukaFilter} onChange={(event) => setTalukaFilter(event.target.value)}><option value="ALL">All Talukas</option>{talukas.map((taluka) => <option key={taluka} value={taluka}>{taluka}</option>)}</select></label>
            <label><span>CEO Status</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="ALL">All Status</option>{Array.from(new Set(rows.map((item) => item.approval))).sort().map((status) => <option key={status} value={status}>{human(status)}</option>)}</select></label>
            <label><span>Current Process</span><select value={processFilter} onChange={(event) => setProcessFilter(event.target.value)}><option value="ALL">All Process Stages</option>{processStages.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
            <label><span>Quick View</span><select value={view} onChange={(event) => setView(event.target.value)}><option value="ALL">All Applications</option><option value="REVIEW">Pending CEO Review</option><option value="APPROVED">CEO Approved</option><option value="ALLOCATED">PMC Allotted</option><option value="UNALLOCATED">PMC Unallocated</option><option value="MOU">MOU Executed</option><option value="DPR">DPR Available</option><option value="DISTRICT">District Cleared</option><option value="STATE">State Cleared</option><option value="DCDC">DCDC Cleared</option><option value="COMPLETE">Workflow Complete</option></select></label>
            <button className="ceo-btn outline" type="button" onClick={() => { setSearch(""); setStateFilter("ALL"); setDistrictFilter("ALL"); setTalukaFilter("ALL"); setStatusFilter("ALL"); setProcessFilter("ALL"); setView("ALL"); }}>Reset All</button>
          </div>

          {loading ? (
            <div className="ceo-table-loading">Loading live NAFED application register…</div>
          ) : !filteredRows.length ? (
            <EmptyState title="No matching NAFED application" text="Filters reset karein ya verify karein ki project par nafedProject / projectSource / applicationPortal marker saved hai." />
          ) : registerMode === "GEOGRAPHY" ? (
            <div className="ceo-report-view">
              <div className="ceo-report-toolbar">
                <div>
                  <b>Consolidated Geographical MIS</b>
                  <span>Current filters ke hisab se live state, district aur taluka report.</span>
                </div>
                <div className="ceo-report-actions">
                  <div className="ceo-group-switch">
                    {["STATE", "DISTRICT", "TALUKA"].map((level) => <button key={level} className={groupBy === level ? "active" : ""} type="button" onClick={() => setGroupBy(level)}>{human(level)}-wise</button>)}
                  </div>
                  <button className="ceo-btn primary compact" type="button" onClick={exportGeographyCsv}>Export Report CSV</button>
                </div>
              </div>
              <div className="ceo-report-table-wrap">
                <table className="ceo-report-table">
                  <thead><tr><th>#</th><th>State</th><th>District</th><th>Taluka / Block</th><th>Applications</th><th>Capacity</th><th>CEO Approved</th><th>PMC Allotted</th><th>MOU</th><th>DPR</th><th>District</th><th>State</th><th>DCDC</th><th>Complete</th><th>Progress</th></tr></thead>
                  <tbody>{geographyReport.map((report, index) => (
                    <tr key={report.key}>
                      <td><span className="ceo-report-index">{String(index + 1).padStart(2, "0")}</span></td>
                      <td><b>{report.state}</b></td>
                      <td>{report.district}</td>
                      <td>{report.taluka}</td>
                      <td><strong>{report.applications}</strong><small>{report.documents} files</small></td>
                      <td><strong>{number(report.capacityMt)} MT</strong></td>
                      <td>{report.approved} / {report.applications}</td>
                      <td>{report.allocated} / {report.applications}</td>
                      <td>{report.mou} / {report.applications}</td>
                      <td>{report.dpr} / {report.applications}</td>
                      <td>{report.districtReady} / {report.applications}</td>
                      <td>{report.stateReady} / {report.applications}</td>
                      <td>{report.dcdcReady} / {report.applications}</td>
                      <td><StatusPill value={report.complete === report.applications ? "COMPLETED" : "PENDING"}>{report.complete} / {report.applications}</StatusPill></td>
                      <td><div className="ceo-progress"><span style={{ width: `${report.averageProgress}%` }} /><b>{report.averageProgress}%</b></div></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="ceo-table-wrap">
              <table className="ceo-table">
                <thead><tr><th>#</th><th>Application / PACS</th><th>Geography</th><th>Capacity</th><th>CEO / PMC</th><th>End-to-End Process</th><th>File Readiness</th><th>Updated</th><th>Control</th></tr></thead>
                <tbody>
                  {filteredRows.map((row, index) => (
                    <tr key={row.id} className={selectedId === row.id ? "selected" : ""}>
                      <td data-label="#"><span className="ceo-serial">{String(index + 1).padStart(2, "0")}</span></td>
                      <td data-label="Application / PACS"><button className="ceo-name-link" type="button" onClick={() => openControl(row)}>{row.pacsName}</button><small>{row.applicationId}</small>{row.registrationNo && <em>Reg: {row.registrationNo}</em>}</td>
                      <td data-label="Geography"><b>{row.village || "Village not recorded"}</b><small>{row.taluka || "Taluka not recorded"} · {row.district || "District not recorded"}</small><em>{row.state || "State not recorded"}</em></td>
                      <td data-label="Capacity"><b>{number(row.capacityMt)} MT</b><small>Submitted {dateTime(row.submittedAt)}</small></td>
                      <td data-label="CEO / PMC"><StatusPill value={row.approval} /><small>{row.allocatedPmcName || "PMC not allotted"}</small></td>
                      <td data-label="Process"><WorkflowStrip steps={row.processSteps} compact /><div className="ceo-current-stage"><span>{row.currentStage}</span><b>{row.workflowProgress}%</b></div><div className="ceo-progress"><span style={{ width: `${row.workflowProgress}%` }} /></div></td>
                      <td data-label="File Readiness"><div className="ceo-file-readiness"><button className="ceo-mini-link" type="button" onClick={() => openControl(row, "DOCUMENTS")}>{row.documents.length} Documents</button>{row.dprUrl ? <a className="ceo-mini-link" href={row.dprUrl} target="_blank" rel="noreferrer">DPR Ready</a> : <span>DPR Pending</span>}{row.mouUrl ? <a className="ceo-mini-link" href={row.mouUrl} target="_blank" rel="noreferrer">MOU Ready</a> : <span>MOU Pending</span>}</div></td>
                      <td data-label="Updated"><b>{dateTime(row.updatedAt)}</b><small>{human(row.currentStageKey)}</small></td>
                      <td data-label="Control"><button className="ceo-btn primary compact" type="button" onClick={() => openControl(row)}>Open Complete File</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {selected && (
          <div
            className="ceo-control-modal"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !busy) setSelectedId("");
            }}
          >
          <section className="ceo-control ceo-control-dialog" id="ceo-control-desk" role="dialog" aria-modal="true" aria-label={`${selected.pacsName} control file`}>
            <div className="ceo-control-head">
              <div><span className="ceo-eyebrow">SELECTED OFFICIAL FILE</span><h2>{selected.pacsName}</h2><p>{selected.applicationId} · {selected.location} · {number(selected.capacityMt)} MT</p></div>
              <div className="ceo-control-head-actions"><StatusPill value={selected.approval} /><button className="ceo-btn outline" type="button" onClick={() => printApplication(selected)}>Print / Save File</button><button className="ceo-close" type="button" onClick={() => setSelectedId("")} aria-label="Close">×</button></div>
            </div>

            <div className="ceo-modal-workflow">
              <div><span>Current Process</span><b>{selected.currentStage}</b></div>
              <WorkflowStrip steps={selected.processSteps} />
              <div className="ceo-modal-progress"><span style={{ width: `${selected.workflowProgress}%` }} /><b>{selected.workflowProgress}% Complete</b></div>
            </div>

            <nav className="ceo-tabs">
              {[
                ["SUMMARY", "File Summary"],
                ["PROCESS", "Process Monitor"],
                ["DECISION", "Approval & Allotment"],
                ["EDIT", "Edit Application"],
                ["DOCUMENTS", "Documents"],
                ["LEGAL", "DPR & MOU"],
                ["ACTIVITY", "Audit Trail"],
              ].map(([key, label]) => <button key={key} className={tab === key ? "active" : ""} type="button" onClick={() => setTab(key)}>{label}</button>)}
            </nav>

            <div className="ceo-tab-body">
              {tab === "SUMMARY" && (
                <div className="ceo-summary-grid">
                  <article className="ceo-panel wide"><div className="ceo-panel-title"><span>Application Particulars</span><button className="ceo-btn outline compact" type="button" onClick={() => setTab("EDIT")}>Edit</button></div><div className="ceo-detail-grid">{[
                    ["Application ID", selected.applicationId],
                    ["PACS / Society", selected.pacsName],
                    ["Registration No.", selected.registrationNo || "—"],
                    ["Capacity", number(selected.capacityMt) + " MT"],
                    ["State", selected.state || "—"],
                    ["District", selected.district || "—"],
                    ["Taluka / Block", selected.taluka || "—"],
                    ["Village", selected.village || "—"],
                    ["Full Location", selected.location],
                    ["Current Process", selected.currentStage],
                    ["Workflow Progress", selected.workflowProgress + "%"],
                    ["Submitted", dateTime(selected.submittedAt)],
                  ].map(([label, value]) => <div key={label}><span>{label}</span><b>{value}</b></div>)}</div></article>
                  <article className="ceo-panel"><div className="ceo-panel-title"><span>Current Control</span></div><div className="ceo-control-list"><div><span>CEO Review</span><StatusPill value={selected.approval} /></div><div><span>Allocated PMC</span><b>{selected.allocatedPmcName || "Not allotted"}</b></div><div><span>Documents</span><b>{selected.documents.length} available</b></div><div><span>Last Updated</span><b>{dateTime(selected.updatedAt)}</b></div></div></article>
                  <article className="ceo-panel"><div className="ceo-panel-title"><span>Direct Access</span></div><div className="ceo-direct-actions"><button className="ceo-btn primary" type="button" onClick={() => setTab("DOCUMENTS")}>Open All Documents</button><button className="ceo-btn outline" type="button" onClick={() => setTab("LEGAL")}>Open DPR & MOU</button><button className="ceo-btn outline" type="button" onClick={() => setTab("DECISION")}>Approval & PMC Allotment</button></div></article>
                </div>
              )}

              {tab === "PROCESS" && (
                <article className="ceo-panel process-monitor-panel">
                  <div className="ceo-panel-title"><span>End-to-End Workflow Monitor</span><StatusPill value={selected.currentStageKey === "COMPLETE" ? "COMPLETED" : "ACTIVE"}>{selected.workflowProgress}% Complete</StatusPill></div>
                  <div className="ceo-process-summary"><div><span>Current Stage</span><b>{selected.currentStage}</b></div><div><span>Last Project Update</span><b>{dateTime(selected.updatedAt)}</b></div><div><span>Geographical Office</span><b>{selected.district || "—"}, {selected.state || "—"}</b></div></div>
                  <div className="ceo-process-ledger">
                    {selected.processSteps.map((step, index) => (
                      <div key={step.key} className={step.done ? "done" : step.issue ? "issue" : "pending"}>
                        <i>{step.done ? "✓" : step.issue ? "!" : index + 1}</i>
                        <section><span>Stage {String(index + 1).padStart(2, "0")}</span><b>{step.label}</b><small>{step.done ? "Process completed / forwarded" : step.issue ? "Attention or correction required" : "Pending at this stage"}</small></section>
                        <StatusPill value={step.status} />
                      </div>
                    ))}
                  </div>
                  <p className="ceo-safe-note">Status is read live from the existing project, MOU, DPR and registrar workflow fields. This monitor does not overwrite any stage.</p>
                </article>
              )}

              {tab === "DECISION" && (
                <div className="ceo-two-column">
                  <article className="ceo-panel"><div className="ceo-panel-title"><span>CEO Application Decision</span><StatusPill value={selected.approval} /></div><label className="ceo-field"><span>Decision Remarks</span><textarea rows="5" value={remark} onChange={(event) => setRemark(event.target.value)} placeholder="Approval note or clear query/rejection reason…" /></label><div className="ceo-action-row"><button className="ceo-btn success" type="button" disabled={Boolean(busy)} onClick={() => saveDecision("APPROVED")}>{busy === "decision" ? "Saving…" : "Approve"}</button><button className="ceo-btn warning" type="button" disabled={Boolean(busy)} onClick={() => saveDecision("QUERY_RAISED")}>Raise Query</button><button className="ceo-btn danger" type="button" disabled={Boolean(busy)} onClick={() => saveDecision("REJECTED")}>Reject</button></div><p className="ceo-safe-note">Only CEO review fields are updated. Existing PMC, registrar, DPR, MOU and construction workflow status is not overwritten.</p></article>
                  <article className="ceo-panel pmc-control-panel">
                    <div className="ceo-panel-title"><span>PMC Allocation & Re-allotment</span>{selected.allocatedPmcName && <StatusPill value="ACTIVE">Active</StatusPill>}</div>
                    <div className="ceo-current-box"><span>Current PMC</span><b>{selected.allocatedPmcName || "No PMC allotted"}</b><small>{selected.allocatedPmcUid || "No PMC UID"}</small></div>
                    <label className="ceo-field"><span>Select Approved / Active PMC</span><select value={selectedPmcUid} onChange={(event) => setSelectedPmcUid(event.target.value)}><option value="">Choose PMC…</option>{selected.allocatedPmcUid && !pmcs.some((pmc) => pmc.id === selected.allocatedPmcUid) && <option value={selected.allocatedPmcUid}>{selected.allocatedPmcName || "Current PMC"} · Current legacy allocation</option>}{pmcs.map((pmc) => <option key={pmc.id} value={pmc.id}>{pmcName(pmc)}{pmc.state ? " · " + pmc.state : ""}</option>)}</select></label>
                    <div className="ceo-allocation-sync"><span>Linked allocation records</span><b>{Math.max(selected.allocationRows?.length || 0, 1)}</b><small>Project and every matching allocation record will be synced together.</small></div>
                    <div className="ceo-action-row"><button className="ceo-btn primary" type="button" disabled={Boolean(busy) || !selectedPmcUid} onClick={saveAllocation}>{busy === "allocation" ? "Synchronising…" : selected.allocatedPmcUid && selectedPmcUid !== selected.allocatedPmcUid ? "Change PMC & Sync All Portals" : "Save PMC Allotment"}</button>{selected.allocatedPmcName && <button className="ceo-btn outline danger-text" type="button" disabled={Boolean(busy)} onClick={clearAllocation}>Clear Allocation</button>}</div>
                    <p className="ceo-safe-note">Re-allotment updates the exact active allocation record, linked legacy records and project master in one batch. Application and uploaded files are never deleted.</p>
                  </article>
                </div>
              )}

              {tab === "EDIT" && (
                <article className="ceo-panel"><div className="ceo-panel-title"><span>Controlled Application Edit</span><StatusPill value="SAFE">Workflow Protected</StatusPill></div><div className="ceo-form-grid">{EDIT_FIELDS.map(([key, label]) => <label className={key === "address" ? "ceo-field span-2" : "ceo-field"} key={key}><span>{label}</span>{key === "address" ? <textarea rows="3" value={editForm[key] ?? ""} onChange={(event) => setEditForm((current) => ({ ...current, [key]: event.target.value }))} /> : <input type={key === "email" ? "email" : key === "capacityMt" ? "number" : "text"} value={editForm[key] ?? ""} onChange={(event) => setEditForm((current) => ({ ...current, [key]: event.target.value }))} />}</label>)}</div><div className="ceo-savebar"><div><b>Safe edit mode</b><span>Approval, allocation, registrar, DPR and MOU fields remain protected.</span></div><button className="ceo-btn success" type="button" disabled={Boolean(busy)} onClick={saveApplication}>{busy === "edit" ? "Saving Changes…" : "Save Application Changes"}</button></div></article>
              )}

              {tab === "DOCUMENTS" && (
                <div className="ceo-document-layout">
                  <article className="ceo-panel document-register-panel">
                    <div className="ceo-panel-title"><span>Complete Document Register</span><StatusPill value={selected.documents.length ? "READY" : "PENDING"}>{selected.documents.length} Files</StatusPill></div>
                    {selected.documents.length ? (
                      <div className="ceo-doc-table-wrap">
                        <table className="ceo-doc-table">
                          <thead><tr><th>#</th><th>Document</th><th>Category / Uploaded</th><th>Source</th><th>Actions</th></tr></thead>
                          <tbody>{selected.documents.map((file, index) => (
                            <tr key={file.url}>
                              <td><i>{String(index + 1).padStart(2, "0")}</i></td>
                              <td><b>{file.label}</b><small>{file.fileName || "Official linked file"}</small></td>
                              <td><b>{human(file.category)}</b><small>{file.uploadedAt ? dateTime(file.uploadedAt) : "Original workflow file"}</small></td>
                              <td><span>{human(file.source)}</span></td>
                              <td><div className="ceo-doc-actions"><a className="ceo-btn outline compact" href={file.url} target="_blank" rel="noreferrer">View / Download</a><button className="ceo-btn primary compact" type="button" disabled={Boolean(busy)} onClick={() => prepareDocumentVersion(file)}>Upload New Version</button></div></td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    ) : <EmptyState title="No document URL found" text="Project ke canonical document fields me file publish hote hi yahan automatically dikh jayegi." />}
                  </article>
                  <article className="ceo-panel upload-panel">
                    <div className="ceo-panel-title"><span>{replaceTarget ? "Upload New Document Version" : "Add Official Document"}</span>{replaceTarget && <StatusPill value="READY">Versioning Mode</StatusPill>}</div>
                    {replaceTarget && <div className="ceo-version-target"><span>New version of</span><b>{replaceTarget.label}</b><button type="button" onClick={() => { setReplaceTarget(null); setUploadFile(null); setUploadTitle(""); setUploadCategory("GENERAL"); if (fileInputRef.current) fileInputRef.current.value = ""; }}>Cancel versioning</button></div>}
                    <label className="ceo-field"><span>Document Category</span><select value={uploadCategory} onChange={(event) => setUploadCategory(event.target.value)}>{DOCUMENT_CATEGORIES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
                    <label className="ceo-field"><span>Document Title</span><input value={uploadTitle} onChange={(event) => setUploadTitle(event.target.value)} placeholder="Official document title" /></label>
                    <label className="ceo-file-drop"><input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.zip" onChange={(event) => setUploadFile(event.target.files?.[0] || null)} /><b>{uploadFile ? uploadFile.name : "Choose PDF / Image / ZIP"}</b><span>{uploadFile ? number(uploadFile.size / 1024 / 1024) + " MB" : "Maximum 50 MB"}</span></label>
                    <button className="ceo-btn primary full" type="button" disabled={Boolean(busy) || !uploadFile} onClick={uploadDocument}>{busy === "upload" ? "Uploading & Linking…" : replaceTarget ? "Publish New Version" : "Add to Official Project File"}</button>
                    <p className="ceo-safe-note">{replaceTarget ? "The new version becomes the latest CEO upload. Previous file remains safely available in document history." : "A new file is appended to the project. Existing documents are never deleted or overwritten."}</p>
                  </article>
                </div>
              )}

              {tab === "LEGAL" && (
                <div className="ceo-legal-grid">
                  <article className="ceo-legal-card"><span className="ceo-eyebrow">DETAILED PROJECT REPORT</span><h3>Final / Published DPR</h3><p>{selected.dprUrl ? "The published DPR is available for direct CEO review and download." : "PMC has not published a DPR URL in the canonical project/DPR register yet."}</p><StatusPill value={selected.dprStatus} /><div className="ceo-legal-actions">{selected.dprUrl ? <a className="ceo-btn primary" href={selected.dprUrl} target="_blank" rel="noreferrer">Open / Download DPR</a> : <button className="ceo-btn outline" type="button" onClick={() => { setUploadCategory("DPR"); setTab("DOCUMENTS"); }}>Upload Supporting DPR</button>}</div></article>
                  <article className="ceo-legal-card"><span className="ceo-eyebrow">MEMORANDUM OF UNDERSTANDING</span><h3>Executed MOU</h3><p>{selected.mouUrl ? "The executed MOU is available for direct CEO review and download." : "The final executed MOU has not been published in the canonical MOU/project register yet."}</p><StatusPill value={selected.mouStatus} /><div className="ceo-legal-actions">{selected.mouUrl ? <a className="ceo-btn primary" href={selected.mouUrl} target="_blank" rel="noreferrer">Open / Download MOU</a> : <button className="ceo-btn outline" type="button" onClick={() => { setUploadCategory("MOU"); setTab("DOCUMENTS"); }}>Upload Supporting MOU</button>}</div></article>
                  <article className="ceo-panel span-2"><div className="ceo-panel-title"><span>Legal Document Particulars</span></div><div className="ceo-detail-grid"><div><span>DPR Status</span><b>{human(selected.dprStatus)}</b></div><div><span>MOU Status</span><b>{human(selected.mouStatus)}</b></div><div><span>PMC</span><b>{selected.allocatedPmcName || "Not allotted"}</b></div><div><span>CEO Review</span><b>{human(selected.approval)}</b></div></div></article>
                </div>
              )}

              {tab === "ACTIVITY" && (
                <article className="ceo-panel"><div className="ceo-panel-title"><span>Live Audit Trail</span><StatusPill value={selectedActivities.length ? "READY" : "PENDING"}>{selectedActivities.length} Events</StatusPill></div>{selectedActivities.length ? <div className="ceo-timeline">{selectedActivities.map((item) => <div key={item.id}><i /><section><b>{human(item.action || item.event || item.type || "Activity")}</b><p>{clean(item.remarks || item.reason || item.message || item.description || "Workflow record updated")}</p><span>{dateTime(item.createdAt || item.updatedAt)} · {human(item.performedByRole || item.actorRole || "System")}</span></section></div>)}</div> : <EmptyState title="No linked audit event" text="New CEO actions and linked project activities yahan live appear honge." />}</article>
              )}
            </div>
          </section>
          </div>
        )}
      </main>
      <footer className="ceo-footer"><b>NAFED CEO National Control Desk</b><span>Live data · Role-based control · No application or document is deleted by this dashboard.</span></footer>
    </div>
  );
}
